import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const steps = [
  {
    number: '01',
    title: 'Inquiry',
    description:
      'Submit a brief consultation request through our form. We review every inquiry personally — typically within five business days.',
  },
  {
    number: '02',
    title: 'Consultation',
    description:
      "Selected families are invited to a 30-minute private consultation with our founder. We discuss the student's academic profile, goals, and timeline, and assess fit on both sides.",
  },
  {
    number: '03',
    title: 'Strategic Plan',
    description:
      'If we proceed, we present a tailored engagement: program structure, timeline, milestones, and terms. Engagement is confirmed in writing.',
  },
  {
    number: '04',
    title: 'The Work',
    description:
      'Sessions begin. Progress is reviewed at every stage — through diagnostics, applications, and decisions.',
  },
];

const HowItWorks = () => {
  return (
    <div data-testid="how-it-works-page">
      <Navbar />

      {/* Hero */}
      <section className="pt-32 pb-20 bg-primary text-primary-foreground">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-4xl">
          <div className="text-sm font-medium tracking-[0.2em] uppercase text-accent mb-6">
            The Process
          </div>
          <h1 className="text-5xl md:text-6xl font-heading font-semibold tracking-tight leading-[1.05] mb-8">
            A deliberate process. By design.
          </h1>
          <p className="text-lg md:text-xl leading-relaxed text-primary-foreground/80 max-w-2xl">
            The founding cohort is capped at twelve students. Our process is selective — designed to ensure mutual fit before any commitment is made on either side.
          </p>
        </div>
      </section>

      {/* Process Steps */}
      <section className="py-24 md:py-32 bg-background">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-5xl">
          <div className="space-y-16 md:space-y-20">
            {steps.map((step, idx) => (
              <div
                key={step.number}
                data-testid={`step-${idx + 1}`}
                className="grid grid-cols-12 gap-6 md:gap-12 items-start border-b border-border/40 pb-16 last:border-0 last:pb-0"
              >
                <div className="col-span-12 md:col-span-3">
                  <div className="font-heading text-6xl md:text-7xl text-accent font-semibold leading-none">
                    {step.number}
                  </div>
                </div>
                <div className="col-span-12 md:col-span-9">
                  <h2 className="text-3xl md:text-4xl font-heading font-semibold text-primary mb-5 tracking-tight">
                    {step.title}
                  </h2>
                  <p className="text-lg leading-relaxed text-muted-foreground max-w-2xl">
                    {step.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Selectivity */}
      <section className="py-24 bg-muted">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-3xl text-center">
          <h2 className="text-3xl md:text-4xl font-heading font-semibold text-primary mb-8 tracking-tight">
            On selectivity
          </h2>
          <div className="space-y-6 text-lg leading-relaxed text-muted-foreground">
            <p>
              We accept a limited cohort each cycle — twelve students in our founding year. This is not exclusivity for its own sake. It is the only way to deliver the standard of attention this work requires.
            </p>
            <p>
              Not every inquiry results in an engagement. We are equally direct about that on both sides.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-primary text-primary-foreground">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-3xl text-center">
          <h2 className="text-3xl md:text-4xl font-heading font-semibold mb-6 tracking-tight">
            Ready to begin the conversation?
          </h2>
          <p className="text-lg text-primary-foreground/80 mb-10 max-w-xl mx-auto">
            We review every inquiry personally, typically within five business days.
          </p>
          <a
            href="/contact"
            data-testid="how-it-works-cta"
            className="inline-block bg-accent text-accent-foreground px-10 py-4 rounded-sm font-medium tracking-wide hover:bg-accent/90 transition-all"
          >
            Request Consultation
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HowItWorks;
